import React, { useEffect, useCallback } from 'react';
import { bool, func, node, object, objectOf, oneOf, oneOfType, shape, string } from 'prop-types';

import { noop } from '@ajaibid/utils';
import { Input } from '@ajaibid/components';
import useField from './hooks/useField';

const FormText = (props) => {
  const {
    id,
    name,
    defaultValue,
    error: propsError,
    showError: propsShowError,
    onChange,
    onError,
    onValid,
    validators,
    ...baseProps
  } = props;

  const [
    fieldValue = '',
    { updateField, updateForm, error: errorMessage, errorKey, showError, setShowError },
  ] = useField(name, { defaultValue, validators });

  const onErrorCb = useCallback((params) => onError(params), [onError]);
  const onValidCb = useCallback((params) => onValid(params), [onValid]);

  useEffect(() => {
    if (errorMessage) {
      onErrorCb({ errorMessage, errorKey });
    } else {
      onValidCb();
    }
  }, [errorMessage, errorKey, onErrorCb, onValidCb]);

  // TODO: this line breaks validator. Wait for QA regression, if it works fine without it, remove
  // useEffect(() => {
  //   if (propsError) {
  //     updateForm(name, fieldValue, propsError);
  //   }
  // }, [propsError, name, updateForm]);

  const handleChange = (event) => {
    if (onChange) {
      onChange(event, { updateField, updateForm });
    } else {
      updateField(event.target.value);
    }
  };

  return (
    <Input.Text
      id={id}
      name={name}
      value={fieldValue}
      onChange={handleChange}
      onBlur={() => !showError && setShowError(true)}
      // Only pass the error message when Form showError is true.
      error={(showError || propsShowError) ? propsError || errorMessage || '' : ''}
      {...baseProps}
    />
  );
};

FormText.propTypes = {
  // <Input.Text> props
  id: string.isRequired,
  name: string.isRequired,
  append: oneOfType([string, node]),
  block: bool,
  bold: bool,
  className: string,
  disabled: bool,
  onAppendClick: func,
  onEnter: func,
  onKeyDown: func,
  onPrependClick: func,
  placeholder: string,
  prepend: oneOfType([string, node]),
  size: string,
  style: object,
  textHint: string,
  type: oneOf(['email', 'number', 'password', 'text']),
  borderColor: string,
  // <Form.Text> props
  defaultValue: string,
  error: string,
  onChange: oneOfType([func, bool]),
  onError: func,
  onValid: func,
  validators: objectOf(
    shape({
      validator: func,
      message: string,
    })
  ),
};

FormText.defaultProps = {
  // <Input.Text> props
  append: '',
  block: false,
  bold: false,
  className: '',
  disabled: false,
  onAppendClick: noop,
  onEnter: noop,
  onKeyDown: noop,
  onPrependClick: noop,
  placeholder: '',
  prepend: '',
  size: 'md',
  style: {},
  textHint: '',
  type: 'text',
  borderColor: '',
  // <Form.Text> props
  defaultValue: '',
  error: '',
  onChange: false,
  onError: noop,
  onValid: noop,
  validators: {},
};

export default FormText;
